// src/screens/BarterKingDetailScreen.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../api';
import './BarterKingDetailScreen.css';
import backIcon from '../assets/backspace-icon.png';

const pretty = (n) => Number(n || 0).toFixed(6).replace(/\.?0+$/, '');

function windowLabel(w) {
  const key = String(w || '').toLowerCase();
  if (key === '7d') return 'Last 7 days';
  if (key === '30d') return 'Last 30 days';
  if (key === '60d') return 'Last 60 days';
  if (key === '90d') return 'Last 90 days';
  return 'Recent';
}

// ✅ Friendly labels for transaction types (user-facing)
function txLabel(typeRaw) {
  const t = String(typeRaw || '').toLowerCase();

  // Tips
  if (t.includes('tip')) return 'Tip';

  // Stakes / visibility holds
  if (t.includes('stake')) return 'Stake';

  // Escrow / trades
  if (t.includes('escrow')) return 'Trade';

  // Wallet funding / cash-in (optional)
  if (t.includes('deposit') || t.includes('fund') || t.includes('topup')) return 'Top up';

  // Withdrawals / cash-out (optional)
  if (t.includes('withdraw')) return 'Withdraw';

  // Fallback
  return 'Transaction';
}

export default function BarterKingDetailScreen() {
  const { username } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const windowParam = searchParams.get('window') || '60d';
  const showRawTypes = (searchParams.get('raw') || '') === '1'; // ✅ admin/debug toggle

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);

  const [paging, setPaging] = useState({ loadingMore: false, nextCursor: null });
  const [tx, setTx] = useState([]);

  const fetchDetail = async (opts = {}) => {
    const { append = false, before = null } = opts;

    try {
      if (!append) {
        setLoading(true);
        setErr(null);
      } else {
        setPaging((p) => ({ ...p, loadingMore: true }));
      }

      const qs = new URLSearchParams();
      qs.set('username', username);
      qs.set('window', windowParam);
      qs.set('limit', '100');
      if (before) qs.set('before', before);

      const res = await api.get(`/badges/barter-king/detail?${qs.toString()}`);
      const payload = res?.data || {};

      if (!append) {
        setData(payload);
        setTx(Array.isArray(payload?.tx) ? payload.tx : []);
        setPaging({ loadingMore: false, nextCursor: payload?.nextCursor || null });
      } else {
        setTx((old) => [...old, ...(Array.isArray(payload?.tx) ? payload.tx : [])]);
        setPaging({ loadingMore: false, nextCursor: payload?.nextCursor || null });
      }
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Failed to load');
    } finally {
      if (!append) setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, windowParam]);

  const totals = data?.totals || {};
  const user = data?.user || { username };

  const bucketRows = useMemo(() => {
    const total = Number(totals.totalOut || 0);
    return (data?.buckets || []).map((b) => {
      const amount = Number(b.amount || 0);
      const pct = total > 0 ? (amount / total) * 100 : 0;
      return {
        key: b.key,
        label: b.label,
        amount,
        pct,
      };
    });
  }, [data, totals.totalOut]);

  return (
    <div className="bkd-page" role="main" aria-label="Barter King">
      {/* Simple sticky header */}
      <div className="bkd-header bkd-header--sticky" aria-label="Header">
        <img
          src={backIcon}
          className="bkd-back"
          alt="Back"
          onClick={() => navigate(-1)}
        />
        <div className="bkd-title-block">
          <h2 className="bkd-title">Barter King</h2>
        </div>
      </div>

      <div className="bkd-scroll" id="bkd-scroll">
        {/* Loading / error */}
        {loading && <div className="bkd-loading">Loading…</div>}
        {!loading && err && <div className="bkd-error">{String(err)}</div>}

        {!loading && !err && (
          <>
            {/* Identity */}

<div className="bkd-user">
  <img
    className="bkd-avatar"
    src={user.profilePic || '/placeholder-profile.png'}
    alt={user.username}
  />
  <div className="bkd-user-meta">
    <div className="bkd-username">@{user.username}</div>
    <div className="bkd-name">{user.name || user.username}</div>
  </div>
  <button
    type="button"
    className="bkd-view-profile"
    onClick={() => navigate(`/user/${encodeURIComponent(user.username)}`)}
  >
    View profile
  </button>
</div>

            {/* Totals card */}
            <div className="bkd-card">
              <div className="bkd-window">
                <strong>{windowLabel(windowParam)}</strong>
              </div>

              <div className="bkd-card-row">
                <div className="bkd-k">Total flow</div>
                <div className="bkd-v">{pretty(totals.totalOut)} Pi</div>
              </div>

              <div className="bkd-card-row">
                <div className="bkd-k">Transactions</div>
                <div className="bkd-v">{totals.txCount || 0}</div>
              </div>

              {totals.lastTxAt && (
                <div className="bkd-card-row">
                  <div className="bkd-k">Last activity</div>
                  <div className="bkd-v">{new Date(totals.lastTxAt).toLocaleString()}</div>
                </div>
              )}
            </div>

            {/* Breakdown bars */}
            <div className="bkd-card">
              <div className="bkd-section-title">Breakdown by category</div>

              <div className="bkd-bars">
                {bucketRows.map((row) => (
                  <div key={row.key} className="bkd-bar-item">
                    <div className="bkd-bar-top">
                      <span>{row.label}</span>
                      <span className="bkd-amt">{pretty(row.amount)} Pi</span>
                    </div>

                    <div className="bkd-bar-rail">
                      <div
                        className="bkd-bar-fill"
                        style={{ width: `${Math.min(100, Math.max(0, row.pct))}%` }}
                      />
                    </div>

                    <div className="bkd-bar-pct">{row.pct.toFixed(1)}%</div>
                  </div>
                ))}

                {bucketRows.length === 0 && (
                  <div className="bkd-empty">No flow in this window.</div>
                )}
              </div>
            </div>

            {/* Transactions */}
            <div className="bkd-card">
              <div className="bkd-section-title">All transactions</div>

              {tx.length === 0 ? (
                <div className="bkd-empty">No transactions.</div>
              ) : (
                <div className="bkd-tx-list">
                  {tx.map((t) => {
                    const label = txLabel(t.type);
                    return (
                      <div className="bkd-tx-row" key={t.id}>
                        <div className="bkd-tx-type">
                          {label}
                          {showRawTypes && t.type ? (
                            <span className="bkd-tx-raw"> • {t.type}</span>
                          ) : null}
                        </div>

                        <div className="bkd-tx-meta">
                          <div className="bkd-tx-amt">{pretty(t.amountAbs)} Pi</div>
                          <div className="bkd-tx-date">
                            {new Date(t.createdAt).toLocaleString()}
                          </div>
                          {t.ref?.toUsername && (
                            <div className="bkd-tx-peer">@{t.ref.toUsername}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {!!paging.nextCursor && (
                <button
                  className="bkd-loadmore"
                  disabled={paging.loadingMore}
                  onClick={() => fetchDetail({ append: true, before: paging.nextCursor })}
                >
                  {paging.loadingMore ? 'Loading…' : 'Load older'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
